import { useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const navItems = [
  { to: '/', label: '首页', icon: 'ri-home-5-line' },
  { to: '/search', label: '搜索', icon: 'ri-search-line' },
  { to: '/about', label: '关于我们', icon: 'ri-information-line' },
  { to: '/contact', label: '联系方式', icon: 'ri-customer-service-2-line' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMobileOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
      isActive
        ? 'bg-primary-50 font-medium text-primary-700'
        : 'text-foreground-700 hover:bg-background-100 hover:text-primary-600'
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-play-circle-fill text-2xl"></i>
          </span>
          <span className="hidden flex-col leading-tight sm:flex">
            <span className="text-base font-bold text-foreground-950">看片软件</span>
            <span className="text-[11px] text-foreground-400">影视播放软件下载平台</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.slice(0, 1).map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} end>
              {item.label}
            </NavLink>
          ))}
          <div className="group relative">
            <button
              type="button"
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
            >
              软件分类
              <i className="ri-arrow-down-s-line text-base"></i>
            </button>
            <div className="invisible absolute left-0 top-full z-50 w-72 translate-y-1 rounded-lg border border-background-200 bg-background-50 p-2 opacity-0 shadow-sm transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-background-100"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${cat.icon} text-lg`}></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
          {navItems.slice(1).map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden max-w-xs flex-1 md:block">
          <div className="flex items-center rounded-md border border-background-300 bg-background-100 px-3 focus-within:border-primary-400">
            <i className="ri-search-line text-base text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索看片软件、播放器…"
              className="w-full bg-transparent px-2 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="打开导航菜单"
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 lg:hidden"
        >
          <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <form onSubmit={submitSearch} className="mb-4 flex items-center rounded-md border border-background-300 bg-background-100 px-3">
            <i className="ri-search-line text-base text-foreground-400"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索看片软件、播放器…"
              className="w-full bg-transparent px-2 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            />
          </form>

          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-home-5-line text-lg text-primary-600"></i>首页
            </Link>

            <button
              type="button"
              onClick={() => setMobileCategoryOpen((v) => !v)}
              className="flex cursor-pointer items-center justify-between rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
            >
              <span className="flex items-center gap-2">
                <i className="ri-apps-2-line text-lg text-primary-600"></i>软件分类
              </span>
              <i className={`${mobileCategoryOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`}></i>
            </button>
            {mobileCategoryOpen && (
              <div className="ml-4 flex flex-col gap-1 border-l border-background-200 pl-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-700 hover:bg-background-100"
                  >
                    <i className={`${cat.icon} text-base text-secondary-600`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}

            {navItems.slice(1).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
              >
                <i className={`${item.icon} text-lg text-primary-600`}></i>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}